import { useEffect, useRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { AlertCircle, CheckCircle2, ImageOff, LoaderCircle, Minus, Plus, RefreshCw, X } from "lucide-react";
import { formatRupiah, statusLabel } from "../lib/format";
import type { Identifier } from "../lib/types";

type ButtonVariant = "primary" | "secondary" | "quiet" | "danger";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  busy?: boolean;
  icon?: ReactNode;
}

export function Button({ variant = "primary", busy, icon, children, className = "", disabled, ...props }: ButtonProps) {
  return (
    <button
      className={`button button--${variant} ${className}`.trim()}
      disabled={disabled || busy}
      {...props}
    >
      {busy ? <LoaderCircle aria-hidden="true" className="spin" size={19} /> : icon}
      <span>{busy ? "Memproses…" : children}</span>
    </button>
  );
}

export function Price({ price, promoPrice, size = "normal" }: { price: number; promoPrice?: number; size?: "normal" | "large" }) {
  const hasPromo = promoPrice !== undefined && promoPrice > 0 && promoPrice < price;
  return (
    <div className={`price price--${size}`} aria-label={hasPromo ? `Harga promo ${formatRupiah(promoPrice)}, harga biasa ${formatRupiah(price)}` : `Harga ${formatRupiah(price)}`}>
      <strong>{formatRupiah(hasPromo ? promoPrice : price)}</strong>
      {hasPromo && <del>{formatRupiah(price)}</del>}
    </div>
  );
}

export function QuantitySelector({ value, max, onChange, label }: { value: number; max: number; onChange: (value: number) => void; label: string }) {
  return (
    <div className="quantity" aria-label={`Jumlah ${label}`}>
      <button type="button" onClick={() => onChange(value - 1)} aria-label={`Kurangi ${label}`}>
        <Minus aria-hidden="true" size={18} />
      </button>
      <output aria-live="polite" aria-label={`${value} ${label}`}>{value}</output>
      <button type="button" onClick={() => onChange(value + 1)} disabled={value >= max} aria-label={`Tambah ${label}`}>
        <Plus aria-hidden="true" size={18} />
      </button>
    </div>
  );
}

export function MediaImage({ mediaId, alt, className = "", loading = "lazy", fetchPriority = "auto" }: { mediaId?: Identifier; alt: string; className?: string; loading?: "eager" | "lazy"; fetchPriority?: "high" | "low" | "auto" }) {
  if (mediaId === undefined || mediaId === "") {
    return (
      <div className={`media-placeholder ${className}`} role="img" aria-label={`Foto ${alt} belum tersedia`}>
        <ImageOff aria-hidden="true" size={32} />
      </div>
    );
  }
  return <img className={className} src={`/api/media/${encodeURIComponent(String(mediaId))}`} alt={alt} loading={loading} fetchPriority={fetchPriority} width="800" height="800" />;
}

export function StatusBadge({ status }: { status: string }) {
  const danger = ["CANCELLED", "REJECTED", "inactive"].includes(status);
  const success = ["PAID", "COMPLETED", "active"].includes(status);
  const warning = ["NEW", "WAITING_CONFIRMATION", "WAITING_PAYMENT", "WAITING_VERIFICATION", "temporarily_closed"].includes(status);
  const tone = danger ? "danger" : success ? "success" : warning ? "warning" : "info";
  return <span className={`status status--${tone}`}>{statusLabel(status)}</span>;
}

export function Skeleton({ rows = 4, cards = false }: { rows?: number; cards?: boolean }) {
  return (
    <div className={cards ? "skeleton-grid" : "skeleton-stack"} aria-label="Sedang memuat" aria-busy="true">
      {Array.from({ length: rows }, (_, index) => (
        <div className={cards ? "skeleton-card" : "skeleton-line"} key={index} />
      ))}
    </div>
  );
}

export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <section className="state-card state-card--empty">
      <div className="state-icon" aria-hidden="true">✦</div>
      <h2>{title}</h2>
      {description && <p>{description}</p>}
      {action}
    </section>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <section className="state-card state-card--error" role="alert">
      <AlertCircle aria-hidden="true" size={28} />
      <h2>Belum berhasil dimuat</h2>
      <p>{message}</p>
      {onRetry && <Button variant="secondary" onClick={onRetry} icon={<RefreshCw aria-hidden="true" size={18} />}>Coba Lagi</Button>}
    </section>
  );
}

export function Notice({ children, tone = "success" }: { children: ReactNode; tone?: "success" | "warning" | "danger" | "info" }) {
  return (
    <div className={`notice notice--${tone}`} role={tone === "danger" ? "alert" : "status"} aria-live="polite">
      {tone === "success" && <CheckCircle2 aria-hidden="true" size={19} />}
      {tone === "danger" && <AlertCircle aria-hidden="true" size={19} />}
      <span>{children}</span>
    </div>
  );
}

export function Modal({ open, title, description, onClose, children }: { open: boolean; title: string; description?: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      className="modal"
      ref={ref}
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="modal__panel">
        <header className="modal__header">
          <div>
            <h2>{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Tutup">
            <X aria-hidden="true" />
          </button>
        </header>
        <div className="modal__body">{children}</div>
      </div>
    </dialog>
  );
}
